import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { X, Save, Plus, Copy, Calculator, Hash } from 'lucide-react'
import toast from 'react-hot-toast'

const unitSchema = z.object({
  unit_ref: z.string().max(100, 'Reference too long').or(z.literal('')).optional(),
  unit_url: z.union([z.string().url('Enter a valid URL'), z.literal('')]).optional(),
  typology: z.string().max(50, 'Typology too long').or(z.literal('')).optional(),
  floor: z.string().max(50, 'Floor too long').or(z.literal('')).optional(),
  fraction: z.string().max(50, 'Fraction too long').or(z.literal('')).optional(),
  status: z.enum(['available', 'reserved', 'sold', 'unknown']).optional(),
  available: z.boolean().optional(),
  private_gpa: z.union([z.number().positive('Must be positive'), z.nan()]).transform((val) => isNaN(val) ? undefined : val).optional(),
  extra_gpa: z.union([z.number().nonnegative('Must be >= 0'), z.nan()]).transform((val) => isNaN(val) ? undefined : val).optional(),
  total_gpa: z.union([z.number().positive('Must be positive'), z.nan()]).transform((val) => isNaN(val) ? undefined : val).optional(),
  current_price: z.union([z.number().positive('Must be positive'), z.nan()]).transform((val) => isNaN(val) ? undefined : val).optional(),
  parking: z.union([z.number().int('Must be an integer').nonnegative('Must be >= 0'), z.nan()]).transform((val) => isNaN(val) ? undefined : val).optional(),
})

type UnitFormData = z.infer<typeof unitSchema>

interface UnitCreateModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: number
  initialData?: Partial<UnitFormData>
  mode?: 'create' | 'clone'
  onSuccess?: () => void
}

export function UnitCreateModal({ isOpen, onClose, projectId, initialData, mode = 'create', onSuccess }: UnitCreateModalProps) {
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    watch,
    setValue
  } = useForm<UnitFormData>({
    resolver: zodResolver(unitSchema),
    mode: 'onChange',
    defaultValues: {
      status: 'available',
      available: true,
      ...initialData,
    },
  })

  React.useEffect(() => {
    if (isOpen) {
      reset({
        status: 'available',
        available: true,
        ...initialData,
      })
    }
  }, [isOpen, initialData, reset])

  const createMutation = useMutation({
    mutationFn: async (data: UnitFormData) => {
      const payload: any = {
        project_id: projectId,
        ...data,
      }

      // If total_gpa not provided but private/extra provided, compute it for convenience
      if ((data.private_gpa ?? undefined) !== undefined || (data.extra_gpa ?? undefined) !== undefined) {
        const p = Number(data.private_gpa || 0)
        const e = Number(data.extra_gpa || 0)
        payload.total_gpa = p + e > 0 ? p + e : undefined
      }

      const res = await api.post('/units', payload)
      return res.data
    },
    onSuccess: () => {
      toast.success(mode === 'clone' ? 'Unit cloned successfully' : 'Unit created successfully')
      queryClient.invalidateQueries({ queryKey: ['project-units', String(projectId)] })
      onSuccess?.()
      onClose()
    },
    onError: (error: any) => {
      console.error('Error creating unit', error)
      toast.error(error?.response?.data?.message || 'Failed to save unit')
    }
  })

  const onSubmit = (data: UnitFormData) => {
    createMutation.mutate(data)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-tagus-lg w-full max-w-3xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="border-b border-secondary-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  {mode === 'clone' ? <Copy className="h-5 w-5 text-primary-600" /> : <Plus className="h-5 w-5 text-primary-600" />}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-secondary-900">{mode === 'clone' ? 'Clone Unit' : 'Create New Unit'}</h2>
                  <p className="text-secondary-500 text-sm">{mode === 'clone' ? 'Duplicate an existing unit and adjust the details' : 'Add a new unit to this project'}</p>
                </div>
              </div>
              <button onClick={onClose} className="text-secondary-400 hover:text-secondary-600 transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="p-6 space-y-6 max-h-[calc(90vh-120px)] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="form-label">Reference</label>
                  <input className="form-input" placeholder="e.g. A-1" {...register('unit_ref')} />
                  {errors.unit_ref && <p className="mt-1 text-sm text-danger-600">{errors.unit_ref.message}</p>}
                </div>
                <div className="space-y-2">
                  <label className="form-label">Unit URL</label>
                  <input className="form-input" placeholder="https://..." {...register('unit_url')} />
                  {errors.unit_url && <p className="mt-1 text-sm text-danger-600">{errors.unit_url.message}</p>}
                </div>

                <div className="space-y-2">
                  <label className="form-label">Typology</label>
                  <input className="form-input" placeholder="e.g. T2" {...register('typology')} />
                </div>
                <div className="space-y-2">
                  <label className="form-label">Floor</label>
                  <input className="form-input" placeholder="e.g. 3" {...register('floor')} />
                </div>

                <div className="space-y-2">
                  <label className="form-label">Fraction</label>
                  <input className="form-input" placeholder="e.g. B" {...register('fraction')} />
                </div>
                <div className="space-y-2">
                  <label className="form-label">Status</label>
                  <select className="form-input" {...register('status')}>
                    <option value="available">available</option>
                    <option value="reserved">reserved</option>
                    <option value="sold">sold</option>
                    <option value="unknown">unknown</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="form-label">Private GPA (m²)</label>
                  <input className="form-input" type="number" step="0.01" placeholder="e.g. 85" {...register('private_gpa', { valueAsNumber: true })} />
                </div>
                <div className="space-y-2">
                  <label className="form-label">Extra GPA (m²)</label>
                  <input className="form-input" type="number" step="0.01" placeholder="e.g. 12" {...register('extra_gpa', { valueAsNumber: true })} />
                </div>

                <div className="space-y-2">
                  <label className="form-label">Total GPA (m²)</label>
                  <input className="form-input" type="number" step="0.01" placeholder="auto or set" {...register('total_gpa', { valueAsNumber: true })} />
                </div>
                <div className="space-y-2">
                  <label className="form-label">Current Price (€)</label>
                  <input className="form-input" type="number" step="1" placeholder="e.g. 350000" {...register('current_price', { valueAsNumber: true })} />
                </div>

                <div className="space-y-2">
                  <label className="form-label">Parking (count)</label>
                  <input className="form-input" type="number" step="1" placeholder="e.g. 1" {...register('parking', { valueAsNumber: true })} />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-secondary-50 flex items-center justify-end space-x-3">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={Object.keys(errors).length > 0 || createMutation.isPending} className="btn-primary inline-flex items-center">
                <Save className="h-4 w-4 mr-2" />
                {mode === 'clone' ? 'Save Clone' : 'Create Unit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default UnitCreateModal




